import type { ReactNode } from "react";
/**
 * The design's chart primitives: a sparkline, a distribution bar row, and the
 * deterministic series behind them.
 *
 * `series` is a seeded generator, taken from the design. It exists for exactly
 * one purpose here — giving a BLURRED placeholder a believable silhouette so
 * the reader can tell a trend panel from a table at a glance. It must never
 * feed anything a reader can actually read; see Blurred.
 *
 * Real charts drawn from real endpoints use the same `spark` and `bars`, which
 * is the point of keeping them in one place: when the fleet-metrics API lands,
 * the panel loses its blur and keeps its shape.
 */

/** Stable across renders, so a placeholder does not shimmer on every keystroke. */
export function series(key: string, n: number, base: number): number[] {
  let x = 7;
  for (let i = 0; i < key.length; i++) x = (x * 31 + key.charCodeAt(i)) % 999983;
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    x = (x * 1103515245 + 12345) % 2147483648;
    const r = x / 2147483648;
    const trend = 1 + (i / n) * 0.35;
    out.push(Math.max(0, Math.round(base * (0.6 + r * 0.55) * trend * (1 + Math.sin(i / 3.7) * 0.1))));
  }
  return out;
}

export function Spark({
  values,
  height = 30,
  className = "text-brand-500 dark:text-brand-300",
}: {
  values: number[];
  height?: number;
  className?: string;
}) {
  const w = 120;
  const max = Math.max(...values, 1);
  const pts = values
    .map(
      (v, i) =>
        `${((i / Math.max(1, values.length - 1)) * w).toFixed(1)},${(height - (v / max) * (height - 2) - 1).toFixed(1)}`,
    )
    .join(" L");
  return (
    <svg
      viewBox={`0 0 ${w} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
      className={`block w-full ${className}`}
      style={{ height: `${height}px` }}
    >
      <path d={`M0,${height} L${pts} L${w},${height} Z`} fill="currentColor" opacity={0.12} />
      <polyline
        points={pts.replace(/ L/g, " ")}
        fill="none"
        stroke="currentColor"
        strokeWidth={1.5}
        vectorEffect="non-scaling-stroke"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface BarRow {
  label: string;
  value: number;
  note?: string;
  tone?: "brand" | "warn" | "bad" | "mute";
}

/** Horizontal distribution bars — DOM rather than SVG, so text stays selectable. */
export function Bars({ rows, max }: { rows: BarRow[]; max?: number }) {
  const ceiling = max ?? Math.max(...rows.map((r) => r.value), 1);
  const fill = {
    brand: "bg-brand-500",
    warn: "bg-amber-500",
    bad: "bg-red-500",
    mute: "bg-slate-400 dark:bg-slate-500",
  };
  return (
    <div className="flex flex-col gap-2">
      {rows.map((r) => (
        <div key={r.label}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-xs/5 text-slate-950 dark:text-white">{r.label}</span>
            <span className="font-mono text-[10px] tabular-nums text-slate-500 dark:text-slate-400">
              {r.value.toLocaleString()}
              {r.note ? ` · ${r.note}` : ""}
            </span>
          </div>
          <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-950/5 dark:bg-white/10">
            <div
              className={`h-full rounded-full ${fill[r.tone ?? "brand"]}`}
              style={{ width: `${Math.round((r.value / ceiling) * 100)}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** The design's headline tile: label, delta, figure, sparkline, window, note. */
export function MetricTile({
  label,
  value,
  note,
  values,
  bad,
  from,
  to,
  noSeries,
  aside,
}: {
  label: string;
  value: string;
  note: string;
  /**
   * The 30-day series. EMPTY when the endpoint serves one day — the tile then
   * says so in the sparkline's place rather than drawing a line through a
   * single point, which would be decoration pretending to be evidence on the
   * page whose job is spotting a trend.
   */
  values: number[];
  bad?: boolean;
  from: string;
  to: string;
  /** Replaces the trend and the line when there is no series. */
  noSeries?: string;
  /** A badge or tag on the label row. */
  aside?: ReactNode;
}) {
  // Last half against the half before it — the design's own comparison.
  const half = Math.floor(values.length / 2);
  const avg = (x: number[]) => x.reduce((s, v) => s + v, 0) / Math.max(1, x.length);
  const d = Math.round(((avg(values.slice(half)) - avg(values.slice(0, half))) / Math.max(1, avg(values.slice(0, half)))) * 100);
  const arrow = d > 0 ? "▲ +" : d < 0 ? "▼ " : "· ";

  return (
    <div className="rounded-lg bg-slate-50 p-4 ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs/5 text-slate-500 dark:text-slate-400">
          {label}
          {aside}
        </p>
        {values.length > 0 && (
          <span
            title="Last 15 days against the 15 before"
            className={`font-mono text-[10px] tabular-nums ${
              bad && d > 0
                ? "text-red-600 dark:text-red-400"
                : "text-slate-500 dark:text-slate-400"
            }`}
          >
            {arrow}
            {d}%
          </span>
        )}
      </div>
      <p className="mt-1 text-2xl/8 font-semibold tabular-nums text-slate-950 dark:text-white">
        {value}
      </p>
      <div className="mt-2">
        {values.length > 0 ? (
          <Spark
            values={values}
            className={bad ? "text-red-500 dark:text-red-400" : "text-brand-500 dark:text-brand-300"}
          />
        ) : (
          // The sparkline's exact height, so a tile with no series lines up
          // with one that has it rather than sitting short in the same row.
          <p className="flex h-6 items-center text-[10px]/4 text-slate-400 dark:text-slate-500">
            {noSeries}
          </p>
        )}
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2 border-t border-slate-950/5 pt-1 font-mono text-[10px] tabular-nums text-slate-400 dark:border-white/10 dark:text-slate-500">
        <span>{from}</span>
        <span>{to}</span>
      </div>
      <div className="mt-1 flex items-baseline justify-between gap-2 text-[10px]">
        <span className="text-slate-500 dark:text-slate-400">{note}</span>
        <span className="text-slate-400 dark:text-slate-500">daily</span>
      </div>
    </div>
  );
}
